import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { Account } from '@/api/types/account';
import { ApiError, api } from '@/lib/api';

import { useAuth } from './useAuth';

type AccountContextValue = {
  accounts: Account[];
  activeAccountId: number | null;
  isLoading: boolean;
  switch: (id: number) => Promise<void>;
};

export const AccountContext = createContext<AccountContextValue | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const qc = useQueryClient();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const sessionAccountId =
    auth.status === 'authenticated' ? (auth.session.account?.id ?? null) : null;
  const mode = auth.status === 'authenticated' ? auth.mode : 'standalone';

  useEffect(() => {
    if (auth.status !== 'authenticated' || mode !== 'standalone') {
      setAccounts([]);
      setActiveAccountId(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    api
      .get<{ data: Account[] }>('/accounts', 'standalone')
      .then((res) => {
        if (cancelled) return;
        setAccounts(res.data);
        setActiveAccountId((current) => current ?? sessionAccountId);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (!(error instanceof ApiError)) return;
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [auth.status, mode, sessionAccountId]);

  const switchAccount = useCallback(
    async (id: number) => {
      if (mode !== 'standalone') return;
      await api.post<Account>(`/accounts/${id}/switch`, {}, 'standalone');
      qc.clear();
      setActiveAccountId(id);
      await auth.refresh();
    },
    [mode, qc, auth],
  );

  const value = useMemo<AccountContextValue>(
    () => ({
      accounts,
      activeAccountId,
      isLoading,
      switch: switchAccount,
    }),
    [accounts, activeAccountId, isLoading, switchAccount],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}
